import { Directive, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Action } from '@ngrx/store';
import { CDIncoterm, CDLineItemValidation, CDTo } from 'app/core/store/contract-details/contract-details.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { EditFlorderMode, isCombinationMaterial, isPackingMaterial, isPalletMaterial, Material, } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { BaseContainer } from 'app/modules/base/BaseContainer';
import { PanelStatuses } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { getFirst$$, util } from 'app/core/util/util';
import { NotificationService, SubmitFeedbackDialogParameters } from 'app/core/services/notification.service';
import { goAction } from 'app/core/store/router/router.actions';
import { map, takeWhile } from 'rxjs/operators';
import { TemplateState } from '../../../core/store/template/template.interface';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LayoutBreakpoints } from '../../../core/util/layout-breakpoints';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ButtonStatus {
  isVisible: boolean;
  isEnabled: boolean;
}

@Directive()
export abstract class FlorderEditContainer<MATERIAL extends Material> extends BaseContainer {
  backUrl: string;
  to: CDTo;
  incoterm: CDIncoterm;
  lineItemValidation?: CDLineItemValidation;
  definitions: Definitions;
  mode: EditFlorderMode;
  panelStatus: PanelStatuses;
  templateState?: TemplateState;
  busy$ = this.store.select(state => state.ui.busy);
  nextOnEnterPressed$ = new BehaviorSubject(true);

  formsAreValid: {
    all: boolean;
    delivery: boolean;
    detail: boolean;
    planning: boolean;
  } = { planning: false, delivery: false, detail: false, all: false };

  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected dialog: MatDialog = inject(MatDialog);
  private notificationService: NotificationService = inject(NotificationService);
  private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  isBootstrapMobile = false; // TODO remove
  showBack$: Observable<boolean>;

  constructor(defaultBackUrl: string) {
    super();
    this.route.queryParamMap.subscribe(paramMap => {
      this.backUrl = paramMap.get('back') || defaultBackUrl;
    });
    this.store
      .select('definitions')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe(definitions => (this.definitions = definitions as Definitions));

    LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.bootstrapXsOrLess).pipe(takeWhile(() => !this.destroyed)).subscribe(isBootstrapMobile => this.isBootstrapMobile = isBootstrapMobile);

    this.showBack$ = LayoutBreakpoints
      .matches(this.breakpointObserver, `(max-width: 500px)`, `${LayoutBreakpoints.landscape} and ${LayoutBreakpoints.smallScreenOrLess}`)
      .pipe(map(matches => matches && !this.showCancel()));
  }

  formValidChanged(panel, value) {
    this.formsAreValid[panel] = value;
    this.formsAreValid.all = this.formsAreValid.detail && this.formsAreValid.delivery && this.formsAreValid.planning;
    this.cd.markForCheck();
  }

  protected getPanelStatusses(materialEdit: boolean, materialExists: boolean): PanelStatuses {
    const isSuperUser = this.userContext.isSuperUser;
    const panelStatus = {
      customer: {
        nr: 1,
        blockIsVisible: isSuperUser,
        isVisible: true,
        isEdit: (this.mode.state === 'create' || this.mode.state === 'edit' || this.mode.state === 'create-confirm') && this.mode.editWindow === 'customer',
        isEditable: (this.mode.state === 'create-confirm' || this.mode.state === 'edit-confirm') && this.mode.editWindow === null,
        isNew: this.mode.state === 'create',
        isUpdate: this.mode.state === 'create-confirm',
        isView: this.mode.state === 'view',
        createTemplate: this.mode.createTemplate,
        editTemplate: this.mode.editTemplate,
        busy: false,
      },
      delivery: {
        nr: isSuperUser ? 2 : 1,
        blockIsVisible: true,
        // not visible: when create and editWindow is delivery or customer
        isVisible: !(this.mode.state === 'create' && this.mode.editWindow === 'customer'),
        isEdit: (this.mode.state === 'create' || this.mode.state === 'edit' || this.mode.state === 'create-confirm') && this.mode.editWindow === 'delivery',
        isEditable: (this.mode.state === 'create-confirm' || this.mode.state === 'edit-confirm') && this.mode.editWindow === null,
        isNew: this.mode.state === 'create',
        isUpdate: this.mode.state === 'create-confirm',
        isView: this.mode.state === 'view',
        createTemplate: this.mode.createTemplate,
        editTemplate: this.mode.editTemplate,
        busy: false,
      },
      detail: {
        nr: isSuperUser ? 3 : 2,
        blockIsVisible: true,
        // not visible: when create and editWindow is delivery or customer
        isVisible: this.mode.type === 'ccr' || !(this.mode.state === 'create' && (this.mode.editWindow === 'delivery' || this.mode.editWindow === 'customer')),
        isEdit: (this.mode.state === 'create' || this.mode.state === 'edit' || this.mode.state === 'create-confirm') && this.mode.editWindow === 'detail',
        isEditable: (this.mode.state === 'create-confirm' || this.mode.state === 'edit-confirm') && this.mode.editWindow === null,
        isNew: this.mode.state === 'create',
        isUpdate: this.mode.state === 'create-confirm',
        isView: this.mode.state === 'view',
        createTemplate: this.mode.createTemplate,
        editTemplate: this.mode.editTemplate,
        busy: this.mode.saving,
        saving: this.mode.datesLoading,
        showNext: !materialExists,
      },
      material: {
        blockIsVisible: true,
        isVisible: this.mode.state !== 'edit' || this.mode.type === 'ccr',
        isEdit: (this.mode.state === 'create' || this.mode.state === 'edit' || this.mode.state === 'create-confirm') && this.mode.editWindow === 'detail' && materialEdit,
        isEditable: this.mode.state === 'create' || this.mode.state === 'create-confirm',
        isView: this.mode.state === 'view',
        createTemplate: this.mode.createTemplate,
        editTemplate: this.mode.editTemplate,
        isNew: !materialExists,
        isUpdate: materialExists,
        busy: this.mode.saving,
      },
      planning: {
        nr: isSuperUser ? 4 : 3,
        blockIsVisible: true,
        isVisible:
          (this.mode.state === 'create' && this.mode.editWindow === 'planning') ||
          this.mode.state === 'create-confirm' ||
          this.mode.state === 'view' ||
          this.mode.type === 'ccr' ||
          this.mode.state === 'edit' ||
          this.mode.state === 'edit-confirm' || this.mode.state === 'recurrence-dates',
        isEdit: (this.mode.state === 'create' || this.mode.state === 'edit' || this.mode.state === 'create-confirm') && this.mode.editWindow === 'planning',
        isEditable: this.mode.state === 'create-confirm' && this.mode.editWindow === null,
        isNew: this.mode.state === 'create',
        isView: this.mode.state === 'view',
        isUpdate: this.mode.state === 'create-confirm',
        createTemplate: this.mode.createTemplate,
        editTemplate: this.mode.editTemplate,
        busy: this.mode.saving,
        opening: this.mode.state === 'create-confirm' && this.mode.openingWindow === 'planning',
      },
      backButton: {
        isVisible: this.mode.state === 'view',
        isEnabled: true,
      },
      submitButton: {
        isVisible: (this.mode.state === 'create-confirm' || this.mode.state === 'edit-confirm') && this.mode.editWindow === null,
        isEnabled: !this.mode.saving,
      },
      ccr: {
        isEdit: false,
      },
      busy: false,
    };
    panelStatus.busy = panelStatus.planning.opening || panelStatus.planning.busy || panelStatus.detail.busy || panelStatus.detail.saving || panelStatus.delivery.busy;
    return panelStatus;
  }

  protected async openCreateFeedbackDialog(parameters: {
    isSuccess: boolean;
    etmOrderNumber: string;
    salesOrderNumber: string;
    ccrNumber: string;
    moduleRoute: string;
    messageType: string; // is used for custom translations
    incoterm: string;
    showDownloadDocumentButton: boolean;
    showCreateButton: boolean;
    createAction: Action;
    showCopyButton?: boolean;
    copyFunction?: (etmOrderNumber: string) => void;
    otherEtmOrderNumbers?: { etmOrderNumber: string }[];
    moduleRouteOverview?:string;
  }) {
    this.nextOnEnterPressed$.next(false);
    const routeParamMap = await getFirst$$(this.route.queryParamMap);
    const backUrl = routeParamMap.get('back') || 'dashboard';
    const params: SubmitFeedbackDialogParameters = {
      isSuccess: parameters.isSuccess,
      messageType: parameters.messageType,
      data: {
        etmOrderNumber: parameters.etmOrderNumber,
        salesOrderNumber: parameters.salesOrderNumber,
        ccrNumber: parameters.ccrNumber,
        incoterm: parameters.incoterm,
        otherEtmOrderNumbers: parameters.otherEtmOrderNumbers,
      },
      buttons: {
        showDownloadDocumentButton: parameters.showDownloadDocumentButton,
        showCreateButton: parameters.showCreateButton,
        showCopyButton: parameters.showCopyButton,
      },
      methods: {
        downloadDocument: this.downloadDocument.bind(this),
        copyFunction: parameters.copyFunction,
        createAction: parameters.createAction,
      },
      route: {
        moduleRoute: parameters.moduleRoute,
        moduleRouteOverview: parameters.moduleRouteOverview,
        backUrl,
      },
    };
    this.notificationService.showSubmitFeedbackDialog(params);
  }

  abstract downloadDocument(documentId: string, extension: string): void;

  protected checkMaterialIsUnique(material: MATERIAL, isForCreateOrder: boolean = true): boolean {
    let lineReferenceIdNotUnique = false;

    const existingMaterials = this.getMaterials().filter(m => {
      let equal = m.type === material.type;
      switch (m.type) {
        case 'pallet':
          if (isPalletMaterial(m) && isPalletMaterial(material)) {
            equal = equal && util.isEqual('' + m.palletId, '' + material.palletId);
            equal = equal && (!isForCreateOrder || util.isEqual('' + m.packingStatus, '' + material.packingStatus));
            equal = equal && (!isForCreateOrder || util.isEqual('' + m.logisticsVarietyPacking, '' + material.logisticsVarietyPacking));
          }
          break;
        case 'packing':
          if (isPackingMaterial(m) && isPackingMaterial(material)) {
            equal = equal && util.isEqual('' + m['lineReferenceId'], '' + material['lineReferenceId']);
            // if lineReferenceId's are used, they should be unique (EPS-3232, RFC-1162)
            if (!util.isNilOrEmpty(m['lineReferenceId']) && !util.isNilOrEmpty(material['lineReferenceId'])) {
              lineReferenceIdNotUnique = true;
              break;
            }
            equal = equal && util.isEqual('' + m.packingId, '' + material.packingId);
            equal = equal && util.isEqual('' + m['contentId'], '' + material['contentId']);
            equal = equal && util.isEqual('' + m.packingStatus, '' + material.packingStatus);
            equal = equal && util.isEqual('' + m.logisticsVarietyPacking, '' + material.logisticsVarietyPacking);
          }
          break;
        case 'combination':
          if (isCombinationMaterial(m) && isCombinationMaterial(material)) {
            equal = equal && util.isEqual('' + m.palletId, '' + material.palletId);
            equal = equal && util.isEqual('' + m.packingId, '' + material.packingId);
            equal = equal && util.isEqual('' + m.packingStatus, '' + material.packingStatus);
            equal = equal && util.isEqual('' + m.logisticsVarietyPacking, '' + material.logisticsVarietyPacking);
            equal = equal && util.isEqual('' + m['packingsPerPallet'], '' + material.packingsPerPallet);
          }
          break;
      }
      return equal;
    });
    if (existingMaterials.length > 2 || (existingMaterials.length === 1 && existingMaterials[0].internalId !== material.internalId)) {
      if (lineReferenceIdNotUnique) {
        this.notificationService.showModal({ type: 'warning', messageCode: 'ORDERS.MATERIAL.NOTIFICATIONS.LINE_REFERENCE_ID_ALREADY_USED', disableClose: false });
      } else {
        this.notificationService.showModal({ type: 'warning', messageCode: 'ORDERS.MATERIAL.NOTIFICATIONS.MATERIAL_ALREADY_EXISTS', disableClose: false });
      }
      return false;
    }
    return true;
  }

  cancelClicked() {
    this.cancel();
  }

  showCancel(): boolean {
    return (
      this.showSubmit()
      && (this.mode.state === 'create-confirm' || this.mode.state === 'edit-confirm')
    );
  }

  showSubmit(): boolean {
    return (
      this.panelStatus.submitButton.isVisible
      && !this.mode.saved
      && (!this.mode.createTemplate || !this.templateState?.saved)
      && (!this.mode.editTemplate || !this.templateState?.saved)
    );
  }

  cancel() {
    this.store.dispatch(goAction({ path: this.backUrl }));
  }

  isCreate(): boolean {
    return this.mode.state === 'create' || this.mode.state === 'create-confirm';
  }

  isEdit(): boolean {
    return this.mode.state === 'edit';
  }

  abstract editDeliveryMethodClicked();

  abstract editDetailClicked();

  abstract editPlanningClicked();

  abstract setDeliveryMethod(form);

  abstract removeMaterial(material: MATERIAL);

  abstract editMaterial(material: MATERIAL);

  abstract saveMaterial(material: MATERIAL);

  abstract getMaterials(): MATERIAL[];

  abstract create(data);

  abstract resetCreatePage();

  abstract saveDetails(form);

  abstract savePlanning(form);

  abstract submitClicked();
}
